import { get, isEmpty } from 'lodash';
import { objectToCamelCase } from './objectTransforms';
import { captureException, captureWarnMessage } from '../tracker/raven';
import type { AuthUserData } from '../store/types';

type DecodedJwt = Record<string, unknown> & {
    email?: string;
    givenName?: string;
    familyName?: string;
    userId?: string | number;
    orgs?: Record<string, unknown>;
};

export const decodeJWT = (jwt: string | null | undefined): DecodedJwt => {
    if (!jwt) {
        return {};
    }

    try {
        if (jwt.split(' ').length > 1) {
            return {};
        }
        const base64 = jwt.split('.')[1].replace('-', '+').replace('_', '/');
        return objectToCamelCase(JSON.parse(window.atob(base64))) as DecodedJwt;
    } catch {
        return {};
    }
};

const decodeToken = (token: string): DecodedJwt => {
    const decoded = decodeJWT(token);

    if (isEmpty(decoded)) {
        const error = new Error('jwt token decode failed');
        error.name = 'JwtTokenDecodeError';
        captureException(error);
        return {};
    }
    return decoded;
};

// sometimes we get users without roles because of setup mistake.
const decodeRole = (decoded: DecodedJwt) => {
    const role = get(decoded, 'resourceAccess.web.roles[0]', '');
    if (!role) {
        captureWarnMessage(`JwtTokenEmptyRoleError: userId=${String(decoded.userId || '')}`);
    }
    return role;
};

export const selectUserFromToken = (token: string): AuthUserData => {
    const decoded = decodeToken(token);

    const orgs = get(decoded, 'orgs', {});
    return {
        email: decoded.email,
        firstName: decoded.givenName,
        lastName: decoded.familyName,
        agencyId: orgs.agencyId,
        locationId: orgs.locationId,
        companyId: orgs.companyId,
        teamId: orgs.teamId,
        role: decodeRole(decoded),
        userId: decoded.userId
    };
};
